import React from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import P from '../texts/p/P';
import EarlyCancellationBanner from './EarlyCancellationBanner';
import {
  formatOriginalDueDateDisplay,
  LoanEarlyCancellationPreview,
  STATUS_CANCELLED_EARLY,
} from '../../utils/earlyCancellationUtils';

export type EarlyCancellationConfirmMode = 'loan' | 'installment' | 'instrument';

export interface EarlyCancellationConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  selectedDate: string;
  mode: EarlyCancellationConfirmMode;
  avalNumber?: string | number | null;
  loanPreview?: LoanEarlyCancellationPreview | null;
  installmentNumber?: number | null;
  totalInstallments?: number | null;
}

const EarlyCancellationConfirmModal: React.FC<EarlyCancellationConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  selectedDate,
  mode,
  avalNumber,
  loanPreview,
  installmentNumber,
  totalInstallments,
}) => {
  const dateLabel = formatOriginalDueDateDisplay(selectedDate);
  const avalLabel = avalNumber ?? '-';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="¿Confirmas la cancelación anticipada?"
      onSecondaryAction={onClose}
      secondaryVariant="secondary"
      secondaryText="Cancelar"
      onPrimaryAction={onConfirm}
      primaryVariant="primary"
      primaryText="Sí, cancelar anticipadamente"
      primaryActionDisabled={isLoading}
      loadingPrimaryBtn={isLoading}
      width="560px"
    >
      <Card margin={{ top: '3', bottom: '3' }}>
        {mode === 'loan' && loanPreview ? (
          <>
            <P type="2" variant="neutral" weight="regular" modifier="regular" withMargin={false}>
              Se va a cancelar anticipadamente el Aval N° {avalLabel} al {dateLabel}.
            </P>
            <Card margin={{ top: '2' }}>
              <P type="3" variant="neutral" weight="regular" modifier="regular" withMargin={false}>
                {loanPreview.reached} cuotas pendientes pasan a estado {STATUS_CANCELLED_EARLY}{' '}
                con vencimiento {dateLabel}.
              </P>
            </Card>
            {loanPreview.skippedPending > 0 && (
              <Card margin={{ top: '2' }}>
                <P type="3" variant="neutral" weight="regular" modifier="regular" withMargin={false}>
                  {loanPreview.skippedPending} cuotas pendientes con vencimiento anterior al{' '}
                  {dateLabel} no se modifican.
                </P>
              </Card>
            )}
            {loanPreview.overdue > 0 && (
              <Card margin={{ top: '2' }}>
                <P type="3" variant="neutral" weight="regular" modifier="regular" withMargin={false}>
                  {loanPreview.overdue} cuotas vencidas quedan sin cambios.
                </P>
              </Card>
            )}
          </>
        ) : mode === 'installment' ? (
          <P type="2" variant="neutral" weight="regular" modifier="regular" withMargin={false}>
            Se va a cancelar anticipadamente la cuota {installmentNumber ?? '-'}/
            {totalInstallments ?? '-'} del Aval N° {avalLabel} al {dateLabel}.
          </P>
        ) : (
          <P type="2" variant="neutral" weight="regular" modifier="regular" withMargin={false}>
            Se va a cancelar anticipadamente el Aval N° {avalLabel} al {dateLabel}.
          </P>
        )}

        <EarlyCancellationBanner variant="warning">
          La fecha de vencimiento original de cada cuota se guarda en Fecha de vencimiento
          original. La acción no se puede deshacer desde la aplicación.
        </EarlyCancellationBanner>
      </Card>
    </Modal>
  );
};

export default EarlyCancellationConfirmModal;
